import { FieldArrayWithId, useFieldArray } from "react-hook-form";
import {
  AssessmentEditFormModel,
  useAssessmentEdit,
} from "../context/AssessmentEditContext";
import { createContext, useCallback, useContext } from "react";
interface Props {
  pageIndex: number;
  lastPage: boolean;
  children: React.ReactNode;
}
export type AssessmentEditPageContextValue = {
  lastPage: boolean;
  pageIndex: number;

  // sections field array
  sectionsFields: FieldArrayWithId<
    AssessmentEditFormModel,
    `paginatedSections.${number}.sections`,
    "id"
  >[];

  // sections field array actions
  appendSection: () => void;
  removeSection: (index: number) => void;

  moveSectionToPage: (data: {
    sectionIndex: number;
    toPageIndex: number;
  }) => void;
  moveQuestionToSection: (data: {
    questionIndex: number;
    fromSectionIndex: number;
    toSectionIndex: number;
  }) => void;
};

const AssessmentEditPageContext = createContext<
  AssessmentEditPageContextValue | undefined
>(undefined);

export function AssessmentEditPageProvider({
  children,
  pageIndex,
  lastPage,
}: Props) {
  const { control, getValues, setValue, hasPages } = useAssessmentEdit();

  const { fields, append, remove } = useFieldArray({
    control,
    name: `paginatedSections.${pageIndex}.sections`,
  });

  const moveQuestionToSection = useCallback(
    ({
      questionIndex,
      fromSectionIndex,
      toSectionIndex,
    }: {
      questionIndex: number;
      fromSectionIndex: number;
      toSectionIndex: number;
    }) => {
      if (fromSectionIndex === toSectionIndex) return;

      const fromName =
        `paginatedSections.${pageIndex}.sections.${fromSectionIndex}.questions` as const;
      const toName =
        `paginatedSections.${pageIndex}.sections.${toSectionIndex}.questions` as const;
      const fromQuestions = getValues(fromName) || [];
      const toQuestions = getValues(toName) || [];
      if (!fromQuestions[questionIndex]) return;
      const [moved] = fromQuestions.splice(questionIndex, 1);
      if (moved) toQuestions.push(moved);
      setValue(fromName, fromQuestions, { shouldDirty: true });
      setValue(toName, toQuestions, { shouldDirty: true });
    },
    [getValues, setValue],
  );

  const moveSectionToPage = useCallback(
    ({
      sectionIndex,
      toPageIndex,
    }: {
      sectionIndex: number;
      toPageIndex: number;
    }) => {
      if (toPageIndex === pageIndex) return;

      const fromName = `paginatedSections.${pageIndex}.sections` as const;
      const toName = `paginatedSections.${toPageIndex}.sections` as const;
      const fromSections = getValues(fromName) || [];
      const toSections = getValues(toName) || [];
      if (!fromSections[sectionIndex]) return;
      const [moved] = fromSections.splice(sectionIndex, 1);
      if (moved) {
        moved.page_nr = toPageIndex + 1;
        toSections.push(moved);
      }

      setValue(fromName, fromSections, {
        shouldDirty: true,
      });
      setValue(toName, toSections, {
        shouldDirty: true,
      });
    },
    [getValues, setValue, pageIndex],
  );

  const appendSection = useCallback(() => {
    append({
      name: "",
      questions: [],
      page_nr: pageIndex + 1,
    });
  }, [append, pageIndex]);
  return (
    <AssessmentEditPageContext.Provider
      value={{
        lastPage,
        pageIndex,
        sectionsFields: fields,
        appendSection,
        removeSection: remove,
        moveSectionToPage,
        moveQuestionToSection,
      }}
    >
      {children}
    </AssessmentEditPageContext.Provider>
  );
}

export function useAssessmentEditPage() {
  const ctx = useContext(AssessmentEditPageContext);
  if (!ctx)
    throw new Error(
      "useAssessmentEditPage must be used within AssessmentEditPageProvider",
    );
  return ctx;
}
